import { useMemo } from 'react';

import {
  END_FUNCTION_NAME,
  START_FUNCTION_NAME,
} from '@polyai/stream/commands/handlers/special-functions/constants';
import { selectEntities } from '@polyai/stream/store/reducers/entities/selectors';
import {
  flowFunctionsSelectors,
  stepsSelectors,
} from '@polyai/stream/store/reducers/flows';
import { selectActiveFunctions } from '@polyai/stream/store/reducers/functions/selectors';
import { selectHandoffs } from '@polyai/stream/store/reducers/handoff/selectors';
import { selectActiveSmsTemplates } from '@polyai/stream/store/reducers/sms/selectors';
import { selectAllTranslations } from '@polyai/stream/store/reducers/translations/selectors';
import { selectVariables } from '@polyai/stream/store/reducers/variables/selectors';
import { selectActiveAttributes } from '@polyai/stream/store/reducers/variant';
import {
  EntityIcon,
  ExitIcon,
  FlowIcon,
  FunctionIcon,
  GlobeIcon,
  NodeTransferIcon,
  SmsIcon,
  StepIcon,
  StepTransitionIcon,
  VariableIcon,
  VariantAttributesIcon,
} from '@polyai/ui/components/atoms/Icons';
import { INTEGRATION_LOGO_MAP } from '@polyai/ui/constants/integrations.constants';

import { useIntegrationConfig } from 'api/queries/useIntegrationConfig';
import {
  ENTITY_PREFIX,
  FLOW_TRANSITION_PREFIX,
  FUNCTION_PREFIX,
  HANDOFF_PREFIX,
  INTEGRATION_PREFIX,
  TRANSLATION_PREFIX,
  TWILIO_SMS_PREFIX,
  VARIABLE_PREFIX,
  VARIANT_ATTRIBUTE_PREFIX,
} from 'components/molecules/LexicalEditor/features/action/constants';
import type {
  ActionPickerConfig,
  OptionGroup,
} from 'components/molecules/LexicalEditor/plugins/ActionPicker/types';
import {
  MenuOptions,
  PickerOption,
  SetupOptions,
} from 'components/molecules/LexicalEditor/plugins/ActionPicker/types';
import { useAppSelector } from 'hooks/reduxHooks';
import { useIsFeatureOn } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import { useIsDemoAccount } from 'hooks/useIsDemoAccount';
import { useParagonSecrets } from 'hooks/useParagonSecrets';
import type { ProjectResource } from 'hooks/useProjectResources';
import {
  PROJECT_RESOURCE,
  useProjectResource,
} from 'hooks/useProjectResources';
import useFlowState from 'screens/FlowDetails/hooks/useFlowState';
import useFlowsState from 'screens/FlowDetails/hooks/useFlowsState';
import { INTEGRATION_LIST } from 'screens/Integrations/constants';
import useCreateFlowFunction from './useCreateFlowFunction';

const MENU_NAME_MAP: Record<MenuOptions, string> = {
  [MenuOptions.FLOWS]: 'Flows',
  [MenuOptions.HANDOFF]: 'Call handoff',
  [MenuOptions.FUNCTIONS]: 'Functions',
  [MenuOptions.STEPS]: 'Steps',
  [MenuOptions.SMS]: 'SMS',
  [MenuOptions.VARIANT_ATTRIBUTES]: 'Attributes',
  [MenuOptions.TRANSITION_FUNCTION]: 'Flow functions',
  [MenuOptions.ENTITIES]: 'Entities',
  [MenuOptions.VARIABLES]: 'Variables',
  [MenuOptions.INTEGRATIONS]: 'Integrations',
  [MenuOptions.TRANSLATIONS]: 'Translations',
};

const GROUPS: Omit<OptionGroup, 'options'>[] = [
  {
    name: MENU_NAME_MAP[MenuOptions.FUNCTIONS],
    singularName: 'function',
    groupId: MenuOptions.FUNCTIONS,
    icon: <FunctionIcon />,
    featureFlag: 'function_calls',
  },
  {
    name: MENU_NAME_MAP[MenuOptions.HANDOFF],
    singularName: 'handoff',
    groupId: MenuOptions.HANDOFF,
    icon: <NodeTransferIcon />,
    featureFlag: 'call_handoff',
  },
  {
    name: MENU_NAME_MAP[MenuOptions.SMS],
    singularName: 'sms_template',
    groupId: MenuOptions.SMS,
    icon: <SmsIcon />,
    featureFlag: 'sms_templates',
  },
  {
    name: MENU_NAME_MAP[MenuOptions.VARIANT_ATTRIBUTES],
    singularName: 'variant_attribute',
    groupId: MenuOptions.VARIANT_ATTRIBUTES,
    icon: <VariantAttributesIcon />,
    featureFlag: 'jupiter_variant_attributes',
  },
  {
    name: MENU_NAME_MAP[MenuOptions.STEPS],
    singularName: 'step',
    groupId: MenuOptions.STEPS,
    icon: <StepIcon />,
    featureFlag: 'jobs_to_be_done',
  },
  {
    name: MENU_NAME_MAP[MenuOptions.TRANSITION_FUNCTION],
    singularName: 'transition_function',
    groupId: MenuOptions.TRANSITION_FUNCTION,
    icon: <StepTransitionIcon />,
    featureFlag: 'jobs_to_be_done',
  },
  {
    name: MENU_NAME_MAP[MenuOptions.FLOWS],
    singularName: 'flow',
    groupId: MenuOptions.FLOWS,
    icon: <FlowIcon />,
    featureFlag: 'jobs_to_be_done',
  },
  {
    name: MENU_NAME_MAP[MenuOptions.ENTITIES],
    singularName: 'entity',
    groupId: MenuOptions.ENTITIES,
    icon: <EntityIcon />,
    featureFlag: 'entity_validation',
  },
  {
    name: MENU_NAME_MAP[MenuOptions.VARIABLES],
    singularName: 'variable',
    groupId: MenuOptions.VARIABLES,
    icon: <VariableIcon />,
    featureFlag: 'variables',
  },
  {
    name: MENU_NAME_MAP[MenuOptions.TRANSLATIONS],
    singularName: 'translation',
    groupId: MenuOptions.TRANSLATIONS,
    icon: <GlobeIcon />,
    featureFlag: 'multilingual_config',
  },
];

const MENU_RESOURCE_MAP: Partial<Record<MenuOptions, ProjectResource>> = {
  [MenuOptions.FUNCTIONS]: PROJECT_RESOURCE.FUNCTIONS,
  [MenuOptions.HANDOFF]: PROJECT_RESOURCE.HANDOFFS,
  [MenuOptions.SMS]: PROJECT_RESOURCE.SMS,
  [MenuOptions.FLOWS]: PROJECT_RESOURCE.FLOWS,
  [MenuOptions.STEPS]: PROJECT_RESOURCE.FLOWS,
  [MenuOptions.TRANSITION_FUNCTION]: PROJECT_RESOURCE.FLOWS,
  [MenuOptions.VARIANT_ATTRIBUTES]: PROJECT_RESOURCE.VARIANT_MANAGEMENT,
  [MenuOptions.VARIABLES]: PROJECT_RESOURCE.FUNCTIONS,
};

export const RESOURCE_KEYS_MAPPING: Partial<
  Record<MenuOptions, { id: string; name: string }>
> = {
  [MenuOptions.FUNCTIONS]: {
    id: 'id',
    name: 'name',
  },
  [MenuOptions.FLOWS]: {
    id: 'id',
    name: 'name',
  },
  [MenuOptions.SMS]: {
    id: 'id',
    name: 'name',
  },
  [MenuOptions.TRANSITION_FUNCTION]: {
    id: 'id',
    name: 'name',
  },
  [MenuOptions.STEPS]: {
    id: 'id',
    name: 'name',
  },
  [MenuOptions.ENTITIES]: {
    id: 'id',
    name: 'name',
  },
  [MenuOptions.VARIABLES]: {
    id: 'id',
    name: 'name',
  },
  [MenuOptions.TRANSLATIONS]: {
    id: 'id',
    name: 'translationKey',
  },
};

export enum ReservedActions {
  CREATE_ACTION = 'create_action',
}

export function usePickerOptions({
  config,
}: {
  config: ActionPickerConfig;
}): OptionGroup[] {
  const handoffs = useAppSelector(selectHandoffs);
  const templates = useAppSelector(selectActiveSmsTemplates);
  const attributes = useAppSelector(selectActiveAttributes);
  const functions = useAppSelector(selectActiveFunctions);
  const entities = useAppSelector(selectEntities);
  const variables = useAppSelector(selectVariables);
  const translations = useAppSelector(selectAllTranslations);
  const { flow } = useFlowState();
  const { flows } = useFlowsState();
  const [addFlowFunction] = useCreateFlowFunction();
  const { data: integrationConfig } = useIntegrationConfig();
  const { isDemoAccount } = useIsDemoAccount();
  const { isFeatureOn: isFeatureOn } = useIsFeatureOn();
  const { isProjectResourceEnabled } = useProjectResource();
  const useSecretsAPI = isFeatureOn('paragon_secrets');
  const { secret } = useParagonSecrets();

  const integrations = useAppSelector(
    state => state?.integrations?.entities ?? {},
  );

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TODO: Cleanup
  const optionGroups = useMemo(() => {
    const enabledMenuOptions = Object.keys(config).reduce(
      (acc, key) => ({
        ...acc,
        [key]: true,
      }),
      {} as Record<MenuOptions, boolean>,
    );

    let currentIndex = 0;
    const getIndex = () => currentIndex++;
    const getOptionsFromResources = <T extends Record<string, any>>({
      data,
      keywords,
      prefix,
      menuOption,
      requiredKeys = { id: 'id', name: 'name' },
      setup,
      transform,
    }: {
      data: T[];
      keywords: string[];
      prefix: string;
      menuOption: MenuOptions;
      requiredKeys?: {
        id: string;
        name: string;
      };
      setup?: SetupOptions;
      transform?: (option: PickerOption) => { id: string; prefix?: string };
    }): PickerOption[] => {
      return data.map(resource => {
        return new PickerOption({
          name: resource[requiredKeys.name],
          id: resource[requiredKeys.id],
          prefix,
          menuOption,
          options: {
            type: 'option',
            keywords,
            index: getIndex(),
            setup,
          },
          transform,
          description: resource.description ?? undefined,
        });
      });
    };

    const buildCreateAction = (
      menu: MenuOptions,
      prefix: string,
      index: number,
    ) => {
      return new PickerOption({
        name: menu,
        id: ReservedActions.CREATE_ACTION,
        prefix,
        menuOption: menu,
        options: {
          type: 'action',
          index,
        },
      });
    };

    // Process standard groups
    const standardGroups = GROUPS.filter(
      group => {
        const resource = MENU_RESOURCE_MAP[group.groupId];
        return (
          enabledMenuOptions[group.groupId] &&
          (!group.featureFlag || isFeatureOn(group.featureFlag)) &&
          (!resource || isProjectResourceEnabled(resource))
        );
      },
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
    ).map(group => {
      const options: PickerOption[] = [];

      switch (group.groupId) {
        case MenuOptions.FUNCTIONS:
          if (config[group.groupId]?.create) {
            options.push(
              buildCreateAction(group.groupId, FUNCTION_PREFIX, getIndex()),
            );
          }
          if (config[group.groupId]?.select) {
            options.push(
              ...getOptionsFromResources({
                data:
                  functions?.filter(
                    f =>
                      !f.archived &&
                      ![START_FUNCTION_NAME, END_FUNCTION_NAME].includes(
                        f.name,
                      ),
                  ) ?? [],
                keywords: ['functions', 'function', 'fn'],
                prefix: FUNCTION_PREFIX,
                requiredKeys: RESOURCE_KEYS_MAPPING[MenuOptions.FUNCTIONS],
                menuOption: group.groupId,
              }),
            );
          }
          break;
        case MenuOptions.HANDOFF:
          if (config[group.groupId]?.create) {
            options.push(
              buildCreateAction(group.groupId, HANDOFF_PREFIX, getIndex()),
            );
          }
          if (config[group.groupId]?.select) {
            options.push(
              ...getOptionsFromResources({
                data: handoffs ?? [],
                keywords: ['handoffs', 'handoff', 'hnd'],
                prefix: HANDOFF_PREFIX,
                menuOption: group.groupId,
              }),
            );
          }
          break;
        case MenuOptions.SMS:
          if (config[group.groupId]?.create) {
            options.push(
              buildCreateAction(group.groupId, TWILIO_SMS_PREFIX, getIndex()),
            );
          }
          if (config[group.groupId]?.select) {
            options.push(
              ...getOptionsFromResources({
                data: templates ?? [],
                keywords: ['sms', 'sms templates', 'templates', 'smtmp'],
                prefix: TWILIO_SMS_PREFIX,
                requiredKeys: RESOURCE_KEYS_MAPPING[MenuOptions.SMS],
                menuOption: group.groupId,
              }),
            );
          }
          break;
        case MenuOptions.VARIANT_ATTRIBUTES:
          if (config[group.groupId]?.create) {
            options.push(
              buildCreateAction(
                group.groupId,
                VARIANT_ATTRIBUTE_PREFIX,
                getIndex(),
              ),
            );
          }
          if (config[group.groupId]?.select) {
            options.push(
              ...getOptionsFromResources({
                data: attributes ?? [],
                keywords: ['variant', 'attribute', 'attr', 'var'],
                prefix: VARIANT_ATTRIBUTE_PREFIX,
                menuOption: group.groupId,
              }),
            );
          }
          break;
        case MenuOptions.STEPS:
          if (config[group.groupId]?.create && flow) {
            options.push(
              buildCreateAction(
                group.groupId,
                FLOW_TRANSITION_PREFIX,
                getIndex(),
              ),
            );
          }
          if (config[group.groupId]?.select && flow) {
            options.push(
              ...getOptionsFromResources({
                data: stepsSelectors.selectAll(flow.steps),
                keywords: ['steps', 'step'],
                prefix: FLOW_TRANSITION_PREFIX,
                requiredKeys:
                  RESOURCE_KEYS_MAPPING[MenuOptions.TRANSITION_FUNCTION],
                menuOption: group.groupId,
                setup: SetupOptions.STEPS,
              }),
            );
          }
          break;
        case MenuOptions.TRANSITION_FUNCTION:
          if (config[group.groupId]?.create && flow) {
            options.push(
              buildCreateAction(
                group.groupId,
                FLOW_TRANSITION_PREFIX,
                getIndex(),
              ),
            );
          }
          if (config[group.groupId]?.select && flow) {
            options.push(
              ...getOptionsFromResources({
                data: flowFunctionsSelectors
                  .selectAll(flow.transitionFunctions)
                  .filter(func => !func.archived),
                keywords: ['transition', 'transition fn'],
                prefix: FLOW_TRANSITION_PREFIX,
                requiredKeys:
                  RESOURCE_KEYS_MAPPING[MenuOptions.TRANSITION_FUNCTION],
                menuOption: group.groupId,
              }),
            );
          }
          if (config[group.groupId]?.create && flow) {
            options.push(
              new PickerOption({
                name: 'Exit flow',
                description: 'Adds a function to exit the flow',
                id: 'exit_flow',
                prefix: FLOW_TRANSITION_PREFIX,
                menuOption: MenuOptions.TRANSITION_FUNCTION,
                icon: <ExitIcon />,
                options: {
                  type: 'option',
                  keywords: ['exit flow'],
                  index: getIndex(),
                  setup: SetupOptions.EXIT_FLOW,
                },
              }),
            );
          }
          break;
        case MenuOptions.FLOWS:
          if (config[group.groupId]?.create) {
            options.push(
              buildCreateAction(group.groupId, FUNCTION_PREFIX, getIndex()),
            );
          }
          if (config[group.groupId]?.select) {
            options.push(
              ...getOptionsFromResources({
                data: flows ?? [],
                keywords: ['flows'],
                prefix: FUNCTION_PREFIX,
                menuOption: group.groupId,
                transform: option => {
                  const flowFunction = addFlowFunction(option.name);
                  return {
                    id: flowFunction.id,
                    prefix: FUNCTION_PREFIX,
                  };
                },
              }),
            );
          }
          break;
        case MenuOptions.ENTITIES:
          if (config[group.groupId]?.create) {
            options.push(
              buildCreateAction(group.groupId, ENTITY_PREFIX, getIndex()),
            );
          }
          if (config[group.groupId]?.select) {
            options.push(
              ...getOptionsFromResources({
                data: entities ?? [],
                keywords: ['entities', 'entity'],
                prefix: ENTITY_PREFIX,
                menuOption: group.groupId,
              }),
            );
          }
          break;
        case MenuOptions.VARIABLES:
          if (config[group.groupId]?.create) {
            options.push(
              buildCreateAction(group.groupId, VARIABLE_PREFIX, getIndex()),
            );
          }
          if (config[group.groupId]?.select) {
            options.push(
              ...getOptionsFromResources({
                data: variables ?? [],
                keywords: ['variables', 'variable', 'vrbl'],
                prefix: VARIABLE_PREFIX,
                requiredKeys: RESOURCE_KEYS_MAPPING[MenuOptions.VARIABLES],
                menuOption: group.groupId,
              }),
            );
          }
          break;
        case MenuOptions.TRANSLATIONS:
          if (config[group.groupId]?.select) {
            options.push(
              ...getOptionsFromResources({
                data: translations ?? [],
                keywords: ['translations', 'translation', 'tn'],
                prefix: TRANSLATION_PREFIX,
                requiredKeys: RESOURCE_KEYS_MAPPING[MenuOptions.TRANSLATIONS],
                menuOption: group.groupId,
              }),
            );
          }
          break;
      }

      return {
        ...group,
        options,
      };
    });

    // Add dynamic integration groups if feature flag is enabled
    const integrationGroups: OptionGroup[] = [];

    // For demo accounts: show integrations only if paragonZoom OR paragonServiceNow flags are enabled
    // For non-demo accounts: show integrations only if paragon_integrations flag is enabled
    const shouldShowIntegrations = isDemoAccount
      ? isFeatureOn('paragonZoom') || isFeatureOn('paragonServiceNow')
      : isFeatureOn('paragon_integrations');

    if (
      shouldShowIntegrations &&
      integrationConfig &&
      config[MenuOptions.INTEGRATIONS]?.select
    ) {
      // Get list of connected providers
      const connectedProviders = new Set<string>();

      // Check secrets API when feature flag is enabled
      if (useSecretsAPI && secret) {
        // Add providers from each environment that have connections
        for (const env of ['sandbox', 'preRelease', 'live']) {
          for (const provider of Object.keys(secret[env])) {
            connectedProviders.add(provider);
          }
        }
      } else {
        // Fall back to Redux (legacy)
        for (const integration of Object.values(integrations)) {
          if (integration?.provider) {
            connectedProviders.add(integration.provider);
          }
        }
      }

      // Create a group for each connected provider that has actions
      for (const provider of integrationConfig.providers) {
        if (
          connectedProviders.has(provider.id) &&
          provider.actions &&
          provider.actions.length > 0
        ) {
          const Logo =
            INTEGRATION_LOGO_MAP[
              provider.id as keyof typeof INTEGRATION_LOGO_MAP
            ];
          const integrationInfo = INTEGRATION_LIST.find(
            i => i.id === provider.id,
          );

          const providerOptions: PickerOption[] = [
            // Add "All actions" option first
            new PickerOption({
              name: `All ${integrationInfo?.name || provider.id} actions`,
              id: `${provider.id}__all`,
              prefix: INTEGRATION_PREFIX,
              menuOption: MenuOptions.INTEGRATIONS,
              description: `Use all available ${integrationInfo?.name || provider.id} actions`,
              options: {
                type: 'option',
                keywords: [
                  'integration',
                  provider.id,
                  integrationInfo?.name || provider.id,
                  'all',
                  'all actions',
                ],
                index: getIndex(),
              },
            }),
            // Then add individual actions
            ...provider.actions.map(action => {
              return new PickerOption({
                name: action.display_name,
                id: action.function_name,
                prefix: INTEGRATION_PREFIX,
                menuOption: MenuOptions.INTEGRATIONS,
                description: action.description,
                options: {
                  type: 'option',
                  keywords: [
                    'integration',
                    provider.id,
                    integrationInfo?.name || provider.id,
                    action.display_name,
                    action.function_name,
                  ],
                  index: getIndex(),
                },
              });
            }),
          ];

          integrationGroups.push({
            name: integrationInfo?.name || provider.id,
            singularName: 'action',
            groupId: MenuOptions.INTEGRATIONS,
            icon: Logo ? <Logo /> : <FunctionIcon />,
            options: providerOptions,
          });
        }
      }
    }

    return [...standardGroups, ...integrationGroups];
  }, [
    config,
    flow,
    functions,
    handoffs,
    templates,
    attributes,
    flows,
    addFlowFunction,
    entities,
    variables,
    translations,
    integrationConfig,
    integrations,
    isFeatureOn,
    isProjectResourceEnabled,
    isDemoAccount,
    useSecretsAPI,
    secret,
  ]);

  return optionGroups;
}
